/* ===========================================================
   NOTIFICATIONS — storefront-styled notifications inbox.
   Modeled on compare-page.jsx for visual parity.
   =========================================================== */
import React from "react";
import { Breadcrumbs, toast } from "@/components/shell";
import { Icons } from "@/lib/icons";
import {
  useNotifications,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
  useDeleteNotification,
} from "@/api/hooks";

const TYPE_ICON = {
  order: "Package",
  enquiry: "Help",
  system: "Sparkle",
  alert: "ShieldCheck",
  verification: "BadgeCheck",
  report: "Calendar",
  message: "Mail",
};

const TYPE_TONE = {
  order:        { fg: "#0a0a0a", bg: "rgba(155, 187, 89, 0.14)" },
  enquiry:      { fg: "#0a0a0a", bg: "rgba(244, 162, 97, 0.18)" },
  alert:        { fg: "#b91c1c", bg: "rgba(185, 28, 28, 0.10)" },
  verification: { fg: "#0a0a0a", bg: "rgba(96, 108, 56, 0.14)" },
  report:       { fg: "#0a0a0a", bg: "rgba(120, 113, 108, 0.14)" },
  message:      { fg: "#0a0a0a", bg: "rgba(2, 132, 199, 0.12)" },
  system:       { fg: "#0a0a0a", bg: "rgba(168, 162, 158, 0.16)" },
};

const FILTERS = [
  ["all",      "All"],
  ["unread",   "Unread"],
  ["order",    "Orders"],
  ["enquiry",  "Enquiries"],
  ["alert",    "Alerts"],
  ["system",   "System"],
];

function timeAgo(iso) {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.round(ms / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return mins + "m ago";
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return hrs + "h ago";
  const days = Math.round(hrs / 24);
  if (days < 7) return days + "d ago";
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short" });
}

function NotificationsPage() {
  const { data: items = [], isLoading } = useNotifications();
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();
  const deleteOne = useDeleteNotification();
  const [filter, setFilter] = React.useState("all");

  const filtered = items.filter((n) => {
    if (filter === "all") return true;
    if (filter === "unread") return !n.read;
    return n.type === filter;
  });

  const unread = items.filter((n) => !n.read).length;

  const handleClick = (n) => {
    if (!n.read) markRead.mutate({ id: n.id });
  };

  return (
    <>
      <Breadcrumbs items={[{ label: "Notifications" }]} />
      <div className="container compare-page">
        <header style={{ padding: "32px 0 24px", display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div>
            <h1 style={{ fontSize: "clamp(28px, 3.6vw, 40px)", letterSpacing: "-0.025em" }}>Notifications</h1>
            <p style={{ color: "var(--ink-3)", fontSize: 15, marginTop: 8 }}>
              {unread > 0
                ? `${unread} unread · auto-archived after 60 days.`
                : "You're all caught up — auto-archived after 60 days."}
            </p>
          </div>
          <button
            className="btn btn-outline btn-sm"
            onClick={() => markAll.mutate(undefined, { onSuccess: () => toast && toast("All marked as read") })}
            disabled={markAll.isPending || unread === 0}
          >
            <Icons.Check size={14} stroke={2} /> Mark all read
          </button>
        </header>

        <div
          style={{
            display: "flex", flexWrap: "wrap", gap: 8,
            padding: "12px 14px", marginBottom: 16,
            background: "var(--paper-2)", border: "1px solid var(--line)",
            borderRadius: 12,
          }}
        >
          {FILTERS.map(([k, label]) => {
            const active = filter === k;
            return (
              <button
                key={k}
                onClick={() => setFilter(k)}
                className={"search-chip" + (active ? " is-active" : "")}
                style={{
                  background: active ? "#0a0a0a" : "var(--paper)",
                  color: active ? "var(--cream)" : "var(--ink-2)",
                  border: "1px solid " + (active ? "#0a0a0a" : "var(--line)"),
                  padding: "6px 12px",
                  borderRadius: 999,
                  fontSize: 12.5,
                  fontWeight: 500,
                  cursor: "pointer",
                }}
              >
                {label}
                {k === "unread" && unread > 0 && (
                  <span style={{ marginLeft: 6, fontFamily: "var(--font-mono)", fontSize: 10, opacity: 0.7 }}>{unread}</span>
                )}
              </button>
            );
          })}
        </div>

        {isLoading ? (
          <div className="empty"><div className="empty-title">Loading notifications…</div></div>
        ) : filtered.length === 0 ? (
          <div className="empty">
            <div className="empty-ic"><Icons.Bell size={26} stroke={1.6} /></div>
            <div className="empty-title">
              {filter === "all" ? "You're all caught up" : "No notifications in this view"}
            </div>
            <div className="empty-desc">
              {filter === "all"
                ? "We'll let you know when there are quote updates, order changes, or delivery events."
                : "Try a different filter to see other notifications."}
            </div>
            {filter !== "all" && (
              <div className="empty-actions">
                <button className="btn btn-outline" onClick={() => setFilter("all")}>Clear filter</button>
              </div>
            )}
          </div>
        ) : (
          <div
            style={{
              display: "flex", flexDirection: "column", gap: 10,
              paddingBottom: 48,
            }}
          >
            {filtered.map((n) => {
              const Icon = Icons[TYPE_ICON[n.type] || "Sparkle"];
              const tone = TYPE_TONE[n.type] || TYPE_TONE.system;
              const Body = n.link ? "a" : "div";
              return (
                <article
                  key={n.id}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "44px 1fr auto auto",
                    gap: 14,
                    padding: "14px 16px",
                    background: n.read ? "var(--paper-2)" : "var(--paper)",
                    border: "1px solid " + (n.read ? "var(--line)" : "rgba(10,10,10,0.14)"),
                    borderRadius: 12,
                    alignItems: "center",
                    position: "relative",
                  }}
                >
                  {!n.read && (
                    <span
                      aria-hidden
                      style={{
                        position: "absolute", left: 6, top: "50%", transform: "translateY(-50%)",
                        width: 6, height: 6, borderRadius: 999, background: "#0a0a0a",
                      }}
                    />
                  )}

                  <div
                    style={{
                      width: 36, height: 36, borderRadius: 8,
                      background: tone.bg, color: tone.fg,
                      display: "inline-flex", alignItems: "center", justifyContent: "center",
                    }}
                  >
                    <Icon size={16} stroke={1.7} />
                  </div>

                  <Body
                    href={n.link || undefined}
                    onClick={() => handleClick(n)}
                    style={{ display: "block", cursor: n.link ? "pointer" : "default" }}
                  >
                    <div style={{ fontWeight: n.read ? 500 : 600, fontSize: 14, color: "#0a0a0a", lineHeight: 1.4 }}>
                      {n.title}
                    </div>
                    {n.body && (
                      <div style={{ fontSize: 13, color: "var(--ink-3)", marginTop: 4, lineHeight: 1.5 }}>
                        {n.body}
                      </div>
                    )}
                  </Body>

                  <div style={{ fontSize: 12, color: "var(--ink-3)", textAlign: "right", whiteSpace: "nowrap", fontFamily: "var(--font-mono)" }}>
                    {timeAgo(n.created_at)}
                  </div>

                  <button
                    className="icon-btn"
                    aria-label="Delete notification"
                    onClick={() => deleteOne.mutate({ id: n.id })}
                    style={{ width: 28, height: 28 }}
                  >
                    <Icons.X size={13} stroke={2} />
                  </button>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}

export { NotificationsPage };
